/**
 * Free Gift System - v6.1 (Qualifying Total Update)
 * Features:
 * 1. CSS Body-Lock (Prevents flicker during re-renders)
 * 2. MutationObserver (Catches new buttons instantly)
 * 3. Debounce & Anti-Loop logic included
 * 4. [NEW] Excludes Gift Cards & Make-up-difference items from threshold calculation
 * 20251229 17:21 by SEVEN (Updated)
 */

if (typeof window.FreeGiftManager === 'undefined') {

  window.FreeGiftManager = class FreeGiftManager {
    constructor() {
      this.selectors = {
        container: '.free-gift-container',
        progressBar: '.fg-progress-bar-inner',
        progressText: '.fg-progress-text',
        giftIcon: '.fg-gift-icon',
        // 结账按钮
        checkoutBtn: 'button[name="checkout"], input[name="checkout"], .cart__checkout-button, #checkout, .checkout-btn, .shopify-payment-button__button',
        // 数量输入和按钮
        quantityInput: '.quantity__input, input[name="updates[]"], input[name="quantity"]',
        quantityBtn: '.quantity__button, button[name="plus"], button[name="minus"], .plus, .minus'
      };

      this.config = {
        thresholds: [], values: [], variants: [], titles: [], images: [],
        currency: 'USD', shopCurrency: 'USD'
      };

      this.state = {
        cartTotal: 0, cartItems: [], rate: 1, isUpdating: false, locale: 'en-US'
      };
      
      this.debounceTimer = null;
      this.observer = null; // DOM 观察者

      // 1. 启动瞬间：立即上锁
      this.toggleGlobalLock(true);
      
      console.log('FG: Manager Initialized v6.1 (Exclusions Active)');
      this.init();
    }

    init() {
      if (!this.readConfig()) {
        let attempts = 0;
        const retryInterval = setInterval(() => {
          attempts++;
          this.toggleGlobalLock(true); // 重试期间持续锁定
          if (this.readConfig() || attempts > 10) {
            clearInterval(retryInterval);
            if (attempts <= 10) this.finishInit();
            else this.toggleGlobalLock(false); // 彻底失败才解锁
          }
        }, 500);
      } else {
        this.finishInit();
      }
    }

    finishInit() {
      this.bindEvents();
      this.initObserver(); // 启动 DOM 观察者
      this.triggerRefresh();
    }

    readConfig() {
      const container = document.querySelector(this.selectors.container);
      if (!container) return false;

      try {
        this.config.thresholds = container.dataset.giftThresholds.split(',').map(Number);
        this.config.values = container.dataset.giftValue.split(',').map(Number);
        this.config.variants = container.dataset.giftVariants.split(',');
        this.config.titles = container.dataset.giftTitles.split(',');
        this.config.images = container.dataset.giftImages.split(',');
        this.config.currency = container.dataset.currencyCode || 'USD';
        this.config.shopCurrency = container.dataset.shopCurrency || 'USD';
        this.setupCurrency();
        return true;
      } catch(e) {
        console.error('FG: Config Error', e);
        return false;
      }
    }

    setupCurrency() {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        this.state.rate = window.Shopify.currency.rate;
      } else {
        this.state.rate = 1;
      }
      const localeMap = { 'USD': 'en-US', 'EUR': 'de-DE', 'GBP': 'en-GB', 'JPY': 'ja-JP', 'HKD': 'en-HK', 'CNY': 'zh-CN' };
      this.state.locale = localeMap[this.config.currency] || 'en-US';
    }

    bindEvents() {
      this.interceptFetch();
      
      document.addEventListener('cart:refresh', () => this.triggerRefresh());
      document.addEventListener('cart:updated', () => this.triggerRefresh());
      document.addEventListener('cart-drawer:open', () => this.triggerRefresh());
      
      // UI 交互立即锁定 (捕获阶段)
      document.body.addEventListener('change', (e) => {
        if (e.target.matches(this.selectors.quantityInput)) this.toggleGlobalLock(true);
      }, true);

      document.body.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.quantityBtn)) this.toggleGlobalLock(true);
        
        // 最后的防线：拦截点击
        if (e.target.closest(this.selectors.checkoutBtn)) {
           // 检查 body 是否有锁，或者 JS 状态是否在更新
           if(document.body.classList.contains('fg-global-lock') || this.state.isUpdating) {
               e.preventDefault();
               e.stopPropagation();
               return false;
           }
        }
      }, true);
    }

    // === MutationObserver (DOM 观察者) ===
    initObserver() {
      if (this.observer) return;

      this.observer = new MutationObserver((mutations) => {
        // 只有当我们处于“更新中”状态，或者 body 被锁定时，才需要干预
        if (this.state.isUpdating || document.body.classList.contains('fg-global-lock')) {
           let shouldDisable = false;
           for (const mutation of mutations) {
             // 检查是否有新节点被添加
             if (mutation.addedNodes.length > 0) {
                shouldDisable = true;
                break;
             }
           }
           // 如果有新元素进来，再次强制执行禁用逻辑
           if (shouldDisable) {
             this.disableCheckoutButtonsDirectly();
           }
        }
      });

      // 观察 body 的子树变化
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    interceptFetch() {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        if(args[0]) {
          const url = args[0].toString();
          if (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change')) {
            this.toggleGlobalLock(true); // 立即上锁
            this.triggerRefresh();
          }
        }
        return response;
      };
    }

    triggerRefresh() {
      this.toggleGlobalLock(true); 
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.refreshCartState();
      }, 500);
    }

    // === 新增方法：计算有效金额（排除礼品卡和补差价） ===
    getQualifyingTotal(items) {
      return items.reduce((sum, item) => {
        // 1. 排除礼品卡
        if (item.product_type === 'Gift Card') return sum;

        // 2. 排除补差价链接
        // 检查 handle, title, product_type 中是否包含关键字
        const handle = (item.handle || '').toLowerCase();
        const title = (item.product_title || '').toLowerCase();
        
        // 关键字列表：根据实际情况增减
        if (handle.includes('price-difference') || title.includes('price difference') || 
            handle.includes('gift-card') || title.includes('gift card')) {
          return sum;
        }

        // 累加有效商品的金额 (使用 final_line_price 处理行级折扣)
        return sum + item.final_line_price;
      }, 0);
    }

    async refreshCartState() {
      if (this.state.isUpdating) return;
      if (this.config.thresholds.length === 0 && !this.readConfig()) {
         this.toggleGlobalLock(false);
         return;
      }

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        this.state.cartItems = cart.items;
        
        // === 修改处：使用过滤后的有效金额 ===
        // 原代码: this.state.cartTotal = cart.total_price;
        this.state.cartTotal = this.getQualifyingTotal(cart.items);
        // =================================

        const plan = this.calculateGiftLogic();

        if (plan) {
          await this.syncGiftInCart(plan);
        } else {
          this.updateAllUIs();
          // 只有在一切就绪后，才解锁
          this.toggleGlobalLock(false);
        }
      } catch (err) {
        console.error('FG: Update Error', err);
        this.toggleGlobalLock(false);
      }
    }

    calculateGiftLogic() {
      const currentThresholds = this.config.thresholds.map(t => Math.round(t * this.state.rate));
      const total = this.state.cartTotal;

      if (total === 0) {
          // 仍然需要检查购物车中是否有旧的赠品需要移除（例如用户移除了主商品）
          const allConfiguredGiftIds = this.config.variants.map(Number);
          let itemsToRemove = [];
          this.state.cartItems.forEach(item => {
              const isGiftProperty = item.properties && item.properties['_gift'] === 'true';
              const isConfiguredGift = allConfiguredGiftIds.includes(item.variant_id);
              if (isGiftProperty || isConfiguredGift) {
                  itemsToRemove.push(item.key);
              }
          });
          
          if (itemsToRemove.length === 0) return null; // 如果没有旧赠品，直接返回
          // 如果有旧赠品需要移除，则继续执行同步逻辑
          return {
              targetVariantId: null, // 不添加新赠品
              targetTitle: '',
              removeKeys: itemsToRemove,
              fixQuantityKeys: []
          };
      }
      
      let targetLevel = -1;
      for (let i = currentThresholds.length - 1; i >= 0; i--) {
        if (total >= currentThresholds[i]) {
          targetLevel = i;
          break;
        }
      }
      const targetVariantId = targetLevel >= 0 ? Number(this.config.variants[targetLevel]) : null;

      const allConfiguredGiftIds = this.config.variants.map(Number);
      let itemsToRemove = [];
      let itemsToFixQuantity = [];
      let hasTargetGift = false;

      this.state.cartItems.forEach(item => {
        const isGiftProperty = item.properties && item.properties['_gift'] === 'true';
        const isConfiguredGift = allConfiguredGiftIds.includes(item.variant_id);

        if (isGiftProperty || isConfiguredGift) {
            if (item.variant_id === targetVariantId) {
                hasTargetGift = true;
                if (item.quantity > 1) itemsToFixQuantity.push(item.key);
            } else {
                itemsToRemove.push(item.key);
            }
        }
      });

      if (itemsToRemove.length === 0 && itemsToFixQuantity.length === 0 && hasTargetGift && targetVariantId) return null;
      if (itemsToRemove.length === 0 && itemsToFixQuantity.length === 0 && !targetVariantId && !hasTargetGift) return null;

      return {
        targetVariantId: hasTargetGift ? null : targetVariantId,
        targetTitle: targetLevel >= 0 ? this.config.titles[targetLevel] : '',
        removeKeys: itemsToRemove,
        fixQuantityKeys: itemsToFixQuantity
      };
    }

    async syncGiftInCart(plan) {
      this.state.isUpdating = true;
      this.toggleGlobalLock(true);
      
      try {
        const updates = {};
        if (plan.removeKeys) plan.removeKeys.forEach(key => updates[key] = 0);
        if (plan.fixQuantityKeys) plan.fixQuantityKeys.forEach(key => updates[key] = 1);
        
        if (Object.keys(updates).length > 0) {
            await fetch('/cart/update.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
        }
        
        if (plan.targetVariantId) {
          await new Promise(r => setTimeout(r, 200));
          await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ 
                  id: plan.targetVariantId, 
                  quantity: 1, 
                  properties: { '_gift': 'true', 'Promo': '🎁 Free Gift' } 
              }]
            })
          });
          this.showNotification(`🎁 Free Gift Added: ${plan.targetTitle}`);
        }

        if (window.location.pathname === '/cart') {
           window.location.reload();
        } else {
           const cartDrawer = document.querySelector('cart-drawer');
           if (cartDrawer) {
              const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
              const data = await res.json();
              if(data['cart-drawer']) {
                  const div = document.createElement('div');
                  div.innerHTML = data['cart-drawer'];
                  const newContent = div.querySelector('cart-drawer');
                  if(newContent) cartDrawer.innerHTML = newContent.innerHTML;
              }
           }
           document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
           
           this.state.isUpdating = false;
           this.triggerRefresh(); 
        }

      } catch (e) { 
        console.error(e);
        this.state.isUpdating = false;
        this.toggleGlobalLock(false);
      }
    }

    showNotification(message) {
       let notification = document.querySelector('.fg-gift-notification');
       if (!notification) {
           notification = document.createElement('div');
           notification.className = 'fg-gift-notification';
           document.body.appendChild(notification);
       }
       notification.textContent = message;
       requestAnimationFrame(() => notification.classList.add('show'));
       setTimeout(() => notification.classList.remove('show'), 3500);
    }

    renderIcons(container) {
      const target = container.querySelector('#fg-gift-icons-target');
      if (!target || target.innerHTML.trim() !== '') return;

      const { thresholds, values, titles, images } = this.config;
      const maxThreshold = thresholds[thresholds.length - 1];

      let html = '';

      thresholds.forEach((threshold, index) => {
        const position = (threshold / maxThreshold) * 100;
        const imgUrl = images[index];
        
        const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-gift"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>`;

        html += `
          <div class="fg-gift-icon" 
               data-base-threshold="${threshold}" 
               data-base-value="${values[index]}" 
               style="left: ${position}%;">
            ${svgIcon}
            <div class="fg-gift-tooltip" data-nosnippet>
              <img src="${imgUrl}" alt="${titles[index]}" loading="lazy">
              <div class="fg-gift-tooltip-info">
                <span>${titles[index]}</span>
                <p class="fg-gift-tooltip-price" data-base-value="${values[index]}">Checking value...</p>
                <p class="fg-gift-tooltip-threshold" data-base-threshold="${threshold}">Checking status...</p>
              </div>
            </div>
          </div>
        `;
      });

      target.innerHTML = html;
    }

    updateAllUIs() {
      const containers = document.querySelectorAll(this.selectors.container);
      const currentThresholds = this.config.thresholds.map(t => Math.round(t * this.state.rate));
      const total = this.state.cartTotal;
      const maxLimit = currentThresholds[currentThresholds.length - 1];

      const formatMoney = (cents) => {
        try { return new Intl.NumberFormat(this.state.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100); } 
        catch(e) { return (cents/100).toFixed(2); }
      };

      // [UPDATED] 1. 计算当前满足的最高层级索引
      let currentLevelIndex = -1;
      for (let i = currentThresholds.length - 1; i >= 0; i--) {
        if (total >= currentThresholds[i]) {
          currentLevelIndex = i;
          break;
        }
      }

      containers.forEach(container => {
        this.renderIcons(container); 

        let percentage = maxLimit > 0 ? Math.min((total / maxLimit) * 100, 100) : 0;
        const bar = container.querySelector(this.selectors.progressBar);
        if (bar) bar.style.width = `${percentage}%`;

        const textEl = container.querySelector(this.selectors.progressText);
        if (textEl) {
            textEl.classList.remove('fg-init-hidden');
            let nextLevelIndex = currentThresholds.findIndex(t => total < t);
            if (nextLevelIndex !== -1) {
                const amountNeeded = currentThresholds[nextLevelIndex] - total;
                textEl.innerHTML = `You're only <strong>${formatMoney(amountNeeded)}</strong> away from <strong>${this.config.titles[nextLevelIndex]}</strong>!`;
            } else {
                textEl.innerHTML = `🎉 Congratulations! You've received the highest level of gifts!`;
            }
        }

        const icons = container.querySelectorAll(this.selectors.giftIcon);
        icons.forEach((icon, idx) => {
          const isUnlocked = total >= currentThresholds[idx];
          
          // [UPDATED] 2. 仅当索引等于当前最高层级时，才添加 active
          // 如果你希望之前的赠品有特殊样式（如打钩），可以使用 .passed 类
          const isCurrent = (idx === currentLevelIndex);
          
          // 切换高亮状态
          icon.classList.toggle('active', isCurrent);
          // 为已解锁但非当前的赠品添加 'passed' 类 (CSS可设置为灰色或其他样式)
          icon.classList.toggle('passed', idx < currentLevelIndex);

          const priceEl = icon.querySelector('.fg-gift-tooltip-price');
          const threshEl = icon.querySelector('.fg-gift-tooltip-threshold');
          if (priceEl) priceEl.textContent = `Value: ${formatMoney(this.config.values[idx] * this.state.rate)}`;
          if (threshEl) threshEl.textContent = isUnlocked ? 'Unlocked!' : `Unlock at ${formatMoney(currentThresholds[idx])}`;
        });
      });
    }

    // === 核心：切换全局锁 ===
    toggleGlobalLock(locked) {
      if (locked) {
          document.body.classList.add('fg-global-lock');
          this.disableCheckoutButtonsDirectly();
      } else {
          if (!this.state.isUpdating) {
              document.body.classList.remove('fg-global-lock');
              this.enableCheckoutButtonsDirectly();
          }
      }
    }

    disableCheckoutButtonsDirectly() {
      const btns = document.querySelectorAll(this.selectors.checkoutBtn);
      btns.forEach(b => {
          b.disabled = true;
          b.classList.add('fg-checkout-disabled');
          b.setAttribute('aria-disabled', 'true');
      });
    }

    enableCheckoutButtonsDirectly() {
      const btns = document.querySelectorAll(this.selectors.checkoutBtn);
      btns.forEach(b => {
          b.disabled = false;
          b.classList.remove('fg-checkout-disabled');
          b.removeAttribute('aria-disabled');
      });
    }
  }; 
}

(function() {
    const start = () => {
        if (!window.freeGiftManager && window.FreeGiftManager) {
            window.freeGiftManager = new window.FreeGiftManager();
        } else if (window.freeGiftManager) {
            window.freeGiftManager.triggerRefresh();
        }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();